import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfirmationService, MessageService} from 'primeng/api';
import {combineLatest} from 'rxjs';
import {MiniBookAdminService} from '../service/mini-book-admin.service';
import {Attendee} from '../shared/Attendee';
import {GeneralConfig} from '../shared/GeneralConfig';
import {Room} from '../shared/Room';
import {Timeslot} from '../shared/Timeslot';
import {TimetableEntry} from '../shared/TimetableEntry';
import { HttpResponse } from '@angular/common/http';

@Component({
    selector: 'edu-udo-itmc-dev-mini-book-attendees',
    templateUrl: './attendees.component.html',
    styleUrls: ['./attendees.component.css'],
    standalone: false
})
export class AttendeesComponent implements OnInit {

  tteId?: number;
  timetableEntry?: TimetableEntry;

  generalConfig?: GeneralConfig;

  rooms: Room[] = [];

  timeslots: Timeslot[] = [];

  allAttendees?: Attendee[];
  filteredAttendees: Attendee[] = [];

  attendees: Attendee[] = [];

  showDialog: boolean = false;
  attendeeSearch: string = '';

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private route: ActivatedRoute,
    private router: Router,
    private adminService: MiniBookAdminService
  ) { }

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('tteId')) {
      this.tteId = +this.route.snapshot.paramMap.get('tteId')!;
      if (Number.isNaN(this.tteId) || this.tteId === 0) {
        this.router.navigate(['timetableEntries']);
      }
    }

    this.adminService.attendees.getAllAttendees().subscribe({
      next: allAttendees => {
        this.allAttendees = allAttendees;
        if (this.tteId) {
          combineLatest([this.adminService.timetableEntries.getTimetableEntry(this.tteId), this.adminService.timetableEntries.getAttendees(this.tteId)]).subscribe({
            next: ([timetableEntry, attendees]) => {
              this.timetableEntry = timetableEntry;
              this.attendees = attendees;
            },
            error: err => {
              console.log(err);
              this.router.navigate(['timetableEntries']);
            }
          });
        } else {
          this.attendees = allAttendees;
        }
      },
      error: err => console.log(err)
    });

    this.updateGeneralConfig();
  }

  updateGeneralConfig() {
    this.adminService.generalConfig.getGeneralConfig().subscribe({
      next: generalConfig => this.generalConfig = generalConfig,
      error: err => console.log(err)
    });
  }

  changeAttendee(attendee: Attendee) {
    this.router.navigate(['.', attendee.id], {relativeTo: this.route});
  }

  removeAttendee(attendee: Attendee) {
    this.confirmationService.confirm({
      accept: () => {
        if (this.timetableEntry) {
          this.adminService.timetableEntries.removeAttendee(this.timetableEntry.id, attendee).subscribe({
            next: timetableEntry => {
              this.attendees = this.attendees.filter(att => att.id !== attendee.id);
              this.timetableEntry = timetableEntry;
            }
          });
        } else {
          this.adminService.attendees.delete(attendee).subscribe({
            next: () => this.attendees = this.attendees.filter(att => att.id !== attendee.id)
          });
        }
      },
      header: 'Bestätigung',
      message: 'Sind Sie sicher, dass Sie die Anmeldung dieses Teilnehmers ' + (this.timetableEntry ? 'für diese Veranstaltung ' : '') + 'entfernen möchten?',
      acceptLabel: 'Löschen',
      acceptIcon: 'pi pi-trash',
      rejectLabel: 'Abbrechen'
    });
  }

  addAttendee(attendee: Attendee) {
    this.adminService.timetableEntries.addAttendee(this.timetableEntry!.id, attendee).subscribe({
      next: timetableEntry => {
        this.timetableEntry = timetableEntry;
        this.attendees.push(attendee);
        this.allAttendees = this.allAttendees?.filter(att => att.id !== attendee.id);
        this.showDialog = false;
      }
    });
  }

  filterAttendees() {
    if (!this.allAttendees) return;
    if (this.attendeeSearch.trim().length <= 0) {
      this.filteredAttendees = this.allAttendees;
    } else {
      this.filteredAttendees = this.allAttendees.filter(attendee =>
        (attendee.firstname.toLowerCase() + ' ' + attendee.lastname.toLowerCase()).includes(this.attendeeSearch.toLowerCase()) ||
        attendee.email.toLowerCase().includes(this.attendeeSearch.toLowerCase())
      );
    }
  }

  hideAddAttendee() {
    if (!this.allAttendees) return;
    this.filteredAttendees = this.allAttendees;
    this.attendeeSearch = '';
  }

  back() {
    history.back();
  }

  deleteAllUsers() {
    this.confirmationService.confirm({
      accept: () => this.adminService.attendees.deleteAllUsers().subscribe(() => this.updateGeneralConfig),
      header: 'Bestätigung',
      message: 'Sind Sie sicher, dass Sie die gesamte Interessiertenliste löschen möchten?',
      acceptLabel: 'Löschen',
      acceptIcon: 'pi pi-trash',
      rejectLabel: 'Abbrechen'
    });
  }

  download() {
    const observer = {
      next: (response: HttpResponse<Blob>) => {
        let filename = response.headers.get('content-disposition')?.split('"')[1];
        const csvUrl = URL.createObjectURL(response.body!);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = csvUrl;
        a.download = filename ?? 'teilnehmer.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(csvUrl);
      },
      error: () => {
        console.log('this should be printed');
        this.messageService.add({severity: 'error', summary: 'Fehler', detail: 'Teilnehmerliste konnte nicht exportiert werden.'})
      }
    };

    if (this.tteId) {
      this.adminService.timetableEntries.downloadAttendeeList(this.tteId).subscribe(observer);
    } else {
      this.adminService.attendees.downloadAttendeeList().subscribe(observer);
    }
  }
}
